import { useEffect, useRef, useState, type FormEvent } from 'react';
import { DISCLAIMER } from '@sc/shared/content';
import icone from '../assets/icone-suncoast.png';
import logo from '../assets/logo-suncoast.png';
import { AnaliseCard } from './AnaliseCard';
import { ContactForm } from './ContactForm';
import { ResumoCard } from './ResumoCard';
import { ReviewsBadge, ReviewsCard, useReviews } from './Reviews';
import { ETAPAS, PASSOS_NUMERICOS, etapaAtual, placeholderDo, type ChatMessage, type ChatState } from './types';
import { useChat } from './useChat';

/** Dentro do balão do site (iframe): sem a coluna lateral, com botão de fechar. */
const EMBED = new URLSearchParams(window.location.search).get('embed') === '1';

/** Avisa a página do site que embute o chat (o widget repassa para o dataLayer e para quem escutar). */
function avisarSite(tipo: 'fechar' | 'lead' | 'fora_do_perfil') {
  if (EMBED && window.parent !== window) window.parent.postMessage({ fonte: 'suncoast-chat', tipo }, '*');
}

export function ChatPage() {
  const chat = useChat();
  const reviews = useReviews();
  const { messages, estado, loading, sending, error } = chat;
  const [texto, setTexto] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const etapa = etapaAtual(estado);
  const encerrada = !!estado?.desfecho;

  useEffect(() => {
    document.title = 'Orçamento de energia solar · Sun Coast';
    document.body.classList.add('chat-page');
    if (EMBED) document.body.classList.add('chat-embed');
  }, []);
  useEffect(() => {
    // Rolagem instantânea: sem animação, mais previsível para quem usa leitor de tela.
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, sending, estado?.desfecho, estado?.linkWhatsapp]);
  useEffect(() => {
    // No celular, focar o campo abre o teclado e cobre os botões: só foca em telas largas.
    if (!sending && !encerrada && window.matchMedia('(min-width: 720px)').matches) inputRef.current?.focus({ preventScroll: true });
  }, [sending, encerrada, messages.length]);
  useEffect(() => {
    if (estado?.desfecho === 'fora_do_perfil') avisarSite('fora_do_perfil');
  }, [estado?.desfecho]);

  const ultimaDoAssistente = [...messages].reverse().find((m) => m.de === 'assistente');

  function enviarTexto(e: FormEvent) {
    e.preventDefault();
    const t = texto.trim();
    if (!t || sending || encerrada) return;
    setTexto('');
    void chat.send({ tipo: 'texto', texto: t }, t);
  }

  function arquivo() {
    if (!sending && !encerrada) void chat.send({ tipo: 'arquivo' });
  }

  function onLink(link: string) {
    chat.setLink(link);
    avisarSite('lead');
  }

  return (
    <div className="chat-root">
      {!EMBED && (
        <aside className="chat-side" aria-label="Sobre o atendimento">
          <a href="https://suncoastenergiasolar.com.br" className="chat-logo">
            <img src={logo} alt="Sun Coast Energia Solar" width="180" height="74" />
          </a>
          <h1 className="chat-title">Orçamento de energia solar em poucos minutos</h1>
          <p className="chat-lead">Responda algumas perguntas e continue com um consultor no WhatsApp. Gratuito e sem compromisso.</p>
          <nav className="chat-steps" aria-label="Etapas">
            <ol>
              {ETAPAS.map((nome, i) => {
                const n = i + 1;
                return (
                  <li key={nome} aria-current={n === etapa ? 'step' : undefined} className={n < etapa ? 'done' : n === etapa ? 'now' : ''}>
                    <span className="chat-step-dot" aria-hidden="true">
                      {n < etapa ? '✓' : n}
                    </span>
                    {nome}
                  </li>
                );
              })}
            </ol>
          </nav>
          {reviews && <ReviewsCard r={reviews} />}
          <ul className="chat-trust">
            <li>
              <strong>+1.000</strong> instalações
            </li>
            <li>
              Financiamento em até <strong>96x</strong>
            </li>
          </ul>
        </aside>
      )}

      <main className="chat-panel" aria-label="Conversa com o assistente da Sun Coast">
        <header className="chat-head">
          <img className="chat-head-icon" src={icone} alt="" width="40" height="40" />
          <div className="chat-head-text">
            <h2>Assistente Sun Coast</h2>
            <p>
              <span className="chat-online" aria-hidden="true" />
              {estado?.linkWhatsapp
                ? 'Tudo certo: continue no WhatsApp'
                : estado?.desfecho === 'qualificado'
                  ? 'Quase lá: falta o seu contato'
                  : `Etapa ${etapa} de ${ETAPAS.length}: ${ETAPAS[etapa - 1]}`}
            </p>
          </div>
          {EMBED && (
            <button type="button" className="chat-close" aria-label="Fechar o chat" onClick={() => avisarSite('fechar')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          )}
        </header>
        <div
          className="chat-progress"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={ETAPAS.length}
          aria-valuenow={etapa}
          aria-label="Progresso"
        >
          <i style={{ width: `${(etapa / ETAPAS.length) * 100}%` }} />
        </div>
        {/* No desktop o cartão completo fica na coluna lateral; aqui, só o selo (celular e balão). */}
        {reviews && (
          <div className="chat-reviews-bar">
            <ReviewsBadge r={reviews} />
          </div>
        )}

        <div className="chat-log" role="log" aria-live="polite" aria-relevant="additions" tabIndex={0} aria-label="Mensagens">
          {loading && <p className="chat-status">Abrindo a conversa…</p>}
          {messages.map((m, i) => (
            <Bubble
              key={m.id}
              m={m}
              escolhido={messages[i + 1]?.de === 'pessoa' ? messages[i + 1]!.texto : undefined}
              ativa={m === ultimaDoAssistente && !sending && !encerrada}
              estado={estado}
              onChip={(valor, rotulo) => void chat.send({ tipo: 'escolha', valor }, rotulo)}
              onLink={onLink}
            />
          ))}
          {sending && (
            <div className="chat-row bot" aria-hidden="true">
              <img className="chat-avatar" src={icone} alt="" width="28" height="28" />
              <div className="chat-bubble chat-typing">
                <i />
                <i />
                <i />
              </div>
            </div>
          )}
          {error && (
            <p className="chat-error chat-banner" role="alert">
              {error}
            </p>
          )}
          <div ref={endRef} />
        </div>

        {encerrada ? (
          <footer className="chat-composer chat-ended">
            {!estado?.linkWhatsapp && (
              <button className="chat-ghost" type="button" onClick={() => void chat.send({ tipo: 'voltar' })} disabled={sending || !estado?.podeVoltar}>
                ← Voltar um passo
              </button>
            )}
            <button className="chat-ghost" type="button" onClick={() => void chat.restart()}>
              Começar de novo
            </button>
          </footer>
        ) : (
          <footer className="chat-composer">
            <form onSubmit={enviarTexto} className="chat-input">
              <label htmlFor="chat-texto" className="sr-only">
                Digite sua resposta
              </label>
              <input
                id="chat-texto"
                ref={inputRef}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder={placeholderDo(estado?.passo)}
                maxLength={300}
                autoComplete="off"
                inputMode={estado && PASSOS_NUMERICOS.has(estado.passo) ? 'decimal' : 'text'}
                enterKeyHint="send"
                disabled={loading || sending}
              />
              <input
                ref={fileRef}
                type="file"
                hidden
                onChange={() => {
                  arquivo();
                  if (fileRef.current) fileRef.current.value = '';
                }}
              />
              <button type="button" className="chat-attach" aria-label="Enviar arquivo" onClick={() => fileRef.current?.click()} disabled={sending}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m21 11-8.5 8.5a5 5 0 0 1-7-7L14 4a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L15 7" />
                </svg>
              </button>
              <button type="submit" className="chat-send" disabled={!texto.trim() || sending || loading}>
                <span className="sr-only">Enviar</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </button>
            </form>
            <div className="chat-tools">
              <button className="chat-ghost" type="button" onClick={() => void chat.send({ tipo: 'voltar' })} disabled={sending || !estado?.podeVoltar}>
                ← Voltar
              </button>
              <button className="chat-ghost" type="button" onClick={() => void chat.send({ tipo: 'encerrar' })} disabled={sending || loading}>
                Encerrar conversa
              </button>
            </div>
          </footer>
        )}
        <p className="chat-disclaimer">{DISCLAIMER}</p>
      </main>
    </div>
  );
}

function Bubble({
  m,
  escolhido,
  ativa,
  estado,
  onChip,
  onLink,
}: {
  m: ChatMessage;
  escolhido?: string;
  ativa: boolean;
  estado: ChatState | null;
  onChip: (valor: string, rotulo: string) => void;
  onLink: (link: string) => void;
}) {
  if (m.de === 'pessoa')
    return (
      <div className="chat-row me">
        <div className="chat-bubble me">{m.texto}</div>
      </div>
    );
  return (
    <>
      <div className="chat-row bot">
        <img className="chat-avatar" src={icone} alt="" width="28" height="28" />
        <div className="chat-bubble">{m.texto}</div>
      </div>
      {m.analise && <AnaliseCard a={m.analise} />}
      {m.resumo && m.resumo.length > 0 && <ResumoCard itens={m.resumo} />}
      {m.chips && (
        <div className="chat-chips" role="group" aria-label="Respostas rápidas">
          {m.chips.map((c) => (
            <button
              key={c.valor + c.rotulo}
              type="button"
              className={`chat-chip${escolhido === c.rotulo ? ' chosen' : ''}`}
              aria-pressed={escolhido === c.rotulo}
              disabled={!ativa}
              onClick={() => onChip(c.valor, c.rotulo)}
            >
              {c.rotulo}
            </button>
          ))}
        </div>
      )}
      {m.formulario && estado?.desfecho === 'qualificado' && (
        <ContactForm link={estado.linkWhatsapp} manutencao={estado.objetivo === 'manutencao'} onDone={onLink} />
      )}
    </>
  );
}
